<template>
  <Card class="cta-card" :style="styles">
    <CardText>
      <VText variant="h2" color="primary" style="margin-bottom: 0.75rem">{{
        title
      }}</VText>
      <p class="error" v-if="errorMessage" aria-live="assertive">
        {{ errorMessage }}
      </p>
      <p class="success" v-if="successMessage" aria-live="polite">
        {{ successMessage }}
      </p>

      <div class="cta-card__grid">
        <div class="cta-card__img">
          <nuxt-img loading="lazy" v-bind="imgProps" />
        </div>

        <form action="#" @submit.prevent="handleSubmit" width="100%">
          <FormGroup>
            <Label for="name">Your name</Label>
            <Input id="name" placeholder="John Smith" v-model="name" required />
          </FormGroup>
          <FormGroup>
            <Label for="email">Your email</Label>
            <Input
              v-model="email"
              type="email"
              id="email"
              placeholder="johnsmith@gmail.com"
              required
            />
          </FormGroup>

          <Button type="submit" color="secondary" :loading="isSending">{{
            buttonText
          }}</Button>
          <VText style="margin-top: 0.5rem; font-size: 0.85rem"
            >* no spam here *</VText
          >
        </form>
      </div>
    </CardText>
  </Card>
</template>

<script>
import tinyColor from 'tinycolor2';
import {
  useTheme,
  Card,
  CardText,
  VText,
  Button,
  FormGroup,
  Label,
  Input,
} from '@campsite-bio/component-lib';
import { computed, ref, useContext } from '@nuxtjs/composition-api';

export default {
  name: 'CtaForm',

  components: {
    Card,
    CardText,
    VText,
    Button,
    FormGroup,
    Label,
    Input,
  },

  props: {
    imgProps: {
      type: Object,
      default: null,
    },

    title: {
      type: String,
      required: true,
    },

    buttonText: {
      type: String,
      required: true,
    },

    endpointUrl: {
      type: String,
      required: true,
    },

    source: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const isSending = ref(false);
    const name = ref(null);
    const email = ref(null);
    const theme = useTheme();
    const successMessage = ref(null);
    const errorMessage = ref(null);

    const { $axios, $sentry } = useContext();

    const styles = computed(() => {
      return {
        '--c-bg-color': tinyColor(theme.value.colors.primary)
          .setAlpha(0.05)
          .toRgbString(),
      };
    });

    async function handleSubmit() {
      if (isSending.value) return;

      isSending.value = true;

      try {
        await $axios.request({
          method: 'post',
          url: 'https://api.campsite.bio/marketing_form_send',
          responseType: 'json',
          data: {
            name: name.value,
            email: email.value,
            source: props.source,
          },
        });

        successMessage.value = `Thanks for your interest! We've sent you an email with a link to download the PDF.`;

        if (window.fbq) {
          window.fbq('track', 'Lead');
        }
      } catch (e) {
        console.error(e);
        $sentry.captureException(e);
        errorMessage.value =
          'There was an error when trying to email the PDF. Please try again soon.';
      }

      isSending.value = false;
    }

    return {
      styles,
      handleSubmit,
      isSending,
      email,
      name,
      successMessage,
      errorMessage,
    };
  },
};
</script>

<style lang="scss" scoped>
.cta-card {
  &__grid {
    display: grid;
    gap: 1rem;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 2fr;
      grid-gap: 2.5rem;
    }
  }

  &__img {
    align-self: center;
    text-align: center;

    img {
      border: none;
      margin: 0;
    }
  }
}

.cta-card.cta-card {
  background: #c2d4a1;
  border-radius: var(--g-border-radius-standard);
  border-radius: Min(
    var(--g-border-radius-standard),
    var(--g-border-radius-min)
  );
  margin: 2rem 0;

  h2 {
    margin: 0;
  }

  ul {
    columns: 2;
    list-style: none;
    margin: 1rem 0 0;
    padding: 0;
  }

  li {
    padding: 0.2rem 0;
  }
}

.success {
  color: var(--g-color-success);
  font-weight: 700;
}

.error {
  color: var(--g-color-error);
  font-weight: 700;
}
</style>
